import {Component, EventEmitter, OnInit} from '@angular/core';
import {MenuItem} from 'primeng/api';
import {CpmCObjectStoreEntryProxyApiModel} from '../../../../../gen/cpm-c-api-ts/src/models/c-customer/cpmc-object-store-entry-proxy.api-model';
import {BaseCpmCApiModel} from '../../../../../gen/cpm-c-api-ts/src/models/cpmc-base.api-model';
import {CpmCObjectStoreEntryProxyApiService} from '../../../../../gen/cpm-c-api-ts/src/services/c-customer/cpmc-object-store-entry-proxy.api-service';
import {GenericTableComponent} from '../../../../../sdk/src/components/generic-table/generic-table.component';
import {
    ETableColDataType,
    ETableColFilterType,
    TableColDef
} from '../../../../../sdk/src/components/generic-table/table-col-def';
import {IBaseModalComponent} from '../../../../../sdk/src/components/ibase-modal/ibase-modal.component';
import {EDialogOpenMode} from '../../../../../sdk/src/enums/dialog-open-mode.enum';
import {PrimeNgImportsModule} from '../../../../../sdk/src/prime-ng-imports';
import {
    ObjectStoreValueDataCellDisplayComponent,
    ObjectStoreValueDataCellDisplayExtra
} from '../../../data-cell-display/object-store-value-cell-display/object-store-value-cell-display.component';
import {SdkBridge} from '../../../sdk-bridge';
import {ObjectStoreEntryUtil} from '../../../utils/object-store-entry.util';
import {NewEditObjectStoreEntryModalResult} from '../new-edit-object-store-entry-modal.component';
import {takeUntil} from 'rxjs';

export class ObjectStoreHistoryModalConfig {
    constructor(
        public entry: CpmCObjectStoreEntryProxyApiModel,
        public linkObjectType: string,
        public linkObjectUUID: string,
        public readOnly = false
    ) {
        // not yet
    }
}

export interface CpmCObjectStoreEntryProxyWithVersionApiModel extends CpmCObjectStoreEntryProxyApiModel {
    version?: number | null; // null version means current
}

export class ObjectStoreHistoryEntryModel extends BaseCpmCApiModel {
    constructor(
        uuid: string,
        created_at: Date,
        created_by_uuid: string | undefined,
        public value: string
    ) {
        super(uuid, created_at, created_by_uuid);
    }
}

@Component({
    selector: 'app-object-store-entry-history',
    templateUrl: './object-store-entry-history.component.html',
    imports: [PrimeNgImportsModule, GenericTableComponent]
})
export class ObjectStoreEntryHistoryComponent
    extends IBaseModalComponent<ObjectStoreEntryHistoryComponent, ObjectStoreHistoryModalConfig>
    implements OnInit
{
    protected menuItems?: MenuItem[] = [];
    protected needRefreshEmitter = new EventEmitter<boolean>();

    // ========================================================

    protected data: CpmCObjectStoreEntryProxyWithVersionApiModel[] = [];
    public tableCols: TableColDef[] = [];

    protected override async onReady(): Promise<void> {
        this.config.width = '60%';
        this.config.height = '80%';
        this.config.header = 'History: ' + this.config.data?.entry.key;
        this.config.closable = true;
        this.config.closeOnEscape = true;
        this.config.resizable = true;
        this.config.modal = true;

        this.initColumns();
        const entry = this.config.data!.entry;
        // push current value to the top
        this.data.push({
            ...entry!,
            version: null
        });
        // then push old values in reversed order to have the oldest value at the bottom
        if (entry?.old_values && entry.old_values.length > 0) {
            for (let i = entry.old_values.length - 1; i >= 0; i--) {
                this.data.push({
                    object_store_uuid: entry.object_store_uuid,
                    key: entry.key,
                    vtype: entry.vtype,
                    schema: entry.schema,
                    attributes: entry.attributes,
                    date: entry.old_dates[i],
                    user_uuid: entry.old_user_uuids![i],
                    value: entry.old_values[i],
                    old_dates: [],
                    old_user_uuids: [],
                    old_values: [],
                    version: i
                });
            }
        }

        this.needRefreshEmitter.pipe(takeUntil(this._destroy$)).subscribe((res) => this.ref.close(res));
    }

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    protected override async onDestroy(): Promise<void> {}

    /**
     * Init the columns (Error handling is done in the parent)
     */
    protected async initColumns(): Promise<void> {
        this.tableCols = [
            {
                field: 'version',
                header: 'Version',
                searcheable: true,
                filterable: true,
                filterType: ETableColFilterType.TEXT,
                dataType: ETableColDataType.TEXT,
                fnCustomData: (row) => {
                    const model = row as CpmCObjectStoreEntryProxyWithVersionApiModel;
                    return model.version === null ? 'current' : 'v' + model.version;
                }
            },
            {
                field: 'date',
                header: 'Modified At',
                searcheable: true,
                filterable: true,
                sortable: true,
                filterType: ETableColFilterType.DATE,
                dataType: ETableColDataType.DATE
            },
            {
                field: 'user_uuid',
                header: 'Modified By',
                dataType: ETableColDataType.DATA_DISPLAY_CELL_COMPONENT,
                dataCellDisplayComponent: SdkBridge.userDataCellDisplayComponent,
                searcheable: false,
                filterable: false
            },
            {
                field: 'value',
                header: 'Value',
                searcheable: false,
                filterable: false,
                dataType: ETableColDataType.DATA_DISPLAY_CELL_COMPONENT,
                dataCellDisplayComponent: ObjectStoreValueDataCellDisplayComponent,
                dataCellDisplayExtra: {
                    linkObjectType: this.config.data?.linkObjectType,
                    linkObjectUUID: this.config.data?.linkObjectUUID,
                    forceRefreshData: false,
                    openDialogMode: EDialogOpenMode.REVERT,
                    updated: this.needRefreshEmitter,
                    initialData: this.config.data?.entry.value
                } as ObjectStoreValueDataCellDisplayExtra,
                fnCustomData: (row) => {
                    // for the version, we want to display "current" for the current value, and "v0", "v1", etc for old values,
                    // so we use the version property we added in CpmCObjectStoreEntryProxyWithVersionApiModel
                    return row.version;
                }
            }
        ];
    }

    /**
     * Build a context menu including the base context menu items
     * @param row Current row
     * @returns Extended content menu items
     */
    protected buildContextMenu(row: unknown) {
        const model = row as CpmCObjectStoreEntryProxyWithVersionApiModel;
        const editAllowed =
            !this.config.data?.readOnly &&
            SdkBridge.isAuthorizedForRouteResourceUpdate(
                CpmCObjectStoreEntryProxyApiService.endpointRevert(
                    this.FAKE_UUID,
                    'placeholder',
                    this.FAKE_UUID,
                    'placeholder',
                    0
                )
            ) &&
            model.version != null &&
            model.version != undefined;

        this.menuItems = [];

        this.menuItems.push({
            label: 'Revert value',
            icon: 'pi pi-arrow-right-arrow-left',
            disabled: !editAllowed,
            command: () => this.onRevertValueClicked(model.version!)
        });
    }

    /**
     * On revert value action clicked,
     * perform the revert action and close the modal with the new entry value as result.
     * @param historyIndex History entry index to restore
     */
    public async onRevertValueClicked(historyIndex: number | null | undefined): Promise<void> {
        if (historyIndex === null || historyIndex === undefined || historyIndex < 0) return;

        const revertedResult = await this.revertByHistoryIndex(historyIndex);
        if (revertedResult) {
            this.ref.close(revertedResult);
        }
    }

    /**
     * Revert current entry to a historical value identified by index.
     * @param historyIndex History entry index in old_values
     */
    public async revertByHistoryIndex(historyIndex: number): Promise<NewEditObjectStoreEntryModalResult | null> {
        const entry = this.config.data!.entry;

        return this.submitRequest(
            () =>
                CpmCObjectStoreEntryProxyApiService.revertObjectStoreEntryProxy(
                    this.config.data!.linkObjectType!,
                    this.config.data!.linkObjectUUID!,
                    entry.key,
                    historyIndex
                ),
            this.getRevertSubmitRequestMessages(historyIndex),
            (resultCRUD: CpmCObjectStoreEntryProxyApiModel) => new NewEditObjectStoreEntryModalResult(resultCRUD)
        );
    }

    /**
     * Get the submit request messages for reverting an entry to a historical value.
     * @param historyIndex History entry index in old_values
     * @returns Submit request messages
     */
    private getRevertSubmitRequestMessages(historyIndex: number) {
        return ObjectStoreEntryUtil.getRevertSubmitRequestMessages(this.config.data!.entry.key, historyIndex);
    }
}
